import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, type OnDestroy } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  EMPTY,
  distinctUntilChanged,
  merge,
  of,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
  type Observable,
} from 'rxjs';
import { SubSink } from 'subsink';
import { objectsEqual } from '../../shared/helpers/objects-equal.helper';
import {
  type InventoryItem,
  type InventoryItemTransition,
  InventoryItemTransitionType,
} from '../../shared/models/api/inventory-item.model';
import { OpaCurrencyPipe } from '../../shared/pipes/opa-currency.pipe';
import { InventoryItemsService } from '../../shared/services/api/inventory-items.service';
import { AuthService } from '../../shared/services/auth.service';
import { MediaQueryService } from '../../shared/services/media-query.service';
import { AssignInventoryItemComponent } from '../inventory-item-list/assign-inventory-item/assign-inventory-item.component';
import { UnassignInventoryItemComponent } from '../inventory-item-list/unassign-inventory-item/unassign-inventory-item.component';
import { inventoryItemTransitionTypeTranslation } from './inventory-item-details.helpers';

@Component({
  selector: 'opa-inventory-item-details',
  imports: [
    CommonModule,
    OpaCurrencyPipe,
    PorscheDesignSystemModule,
    RouterLink,
    LetDirective,
    AssignInventoryItemComponent,
    UnassignInventoryItemComponent,
    TranslocoPipe,
  ],
  templateUrl: './inventory-item-details.component.html',
  styleUrl: './inventory-item-details.component.scss',
})
export class InventoryItemDetailsComponent implements OnDestroy {
  private readonly inventoryItemsService = inject(InventoryItemsService);
  private readonly route = inject(ActivatedRoute);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly isInventoryAdmin = inject(AuthService).isInventoryAdmin();
  readonly mediaMaxXs$ = inject(MediaQueryService).max('xs');
  readonly inventoryItemTransitionTypeTranslation = inventoryItemTransitionTypeTranslation;
  readonly InventoryItemTransitionType = InventoryItemTransitionType;
  private readonly subSink = new SubSink();

  inventoryItem$ = this.getInventoryItem();
  inventoryItemTransitionHistory$ = this.getInventoryItemTransitionHistory();

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  reloadInventoryItem(markForCheck = false): void {
    this.inventoryItem$ = this.getInventoryItem();
    if (markForCheck) this.changeDetectorRef.markForCheck();
  }

  reloadTransitionHistory(): void {
    this.inventoryItemTransitionHistory$ = this.getInventoryItemTransitionHistory();
  }

  downloadWarranty(itemId: number): void {
    this.subSink.sink = this.inventoryItemsService
      .downloadInventoryItemWarranty(itemId)
      .subscribe();
  }

  downloadReceipt(itemId: number): void {
    this.subSink.sink = this.inventoryItemsService.downloadInventoryItemReceipt(itemId).subscribe();
  }

  trackRow(index: number): number {
    return index;
  }

  private getInventoryItem(): Observable<InventoryItem> {
    const routeParams$ = merge(
      of(this.route.snapshot.params),
      this.route.params.pipe(
        startWith(this.route.snapshot.params),
        distinctUntilChanged((a, b) => objectsEqual(a, b)),
        skip(1),
        tap(() => this.reloadInventoryItem(true)),
        switchMap(() => EMPTY),
      ),
    );

    return routeParams$.pipe(
      switchMap((routeParams) => {
        const id = Number(routeParams['id']);
        return this.inventoryItemsService.getInventoryItem(id);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getInventoryItemTransitionHistory(): Observable<InventoryItemTransition[]> {
    const routeParams$ = merge(
      of(this.route.snapshot.params),
      this.route.params.pipe(
        startWith(this.route.snapshot.params),
        distinctUntilChanged((a, b) => objectsEqual(a, b)),
        skip(1),
        tap(() => this.reloadInventoryItem(true)),
        switchMap(() => EMPTY),
      ),
    );

    return routeParams$.pipe(
      switchMap((routeParams) => {
        const id = Number(routeParams['id']);
        return this.inventoryItemsService.getInventoryItemTransitionHistory(id);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
